import { useGetAllAttachments } from "@/api/attachment";
import { useUploadDocument } from "@/api/file";
import { useGetMultipleSampleById } from "@/api/multiplesample";
import {
  administrativeDocumentTypeSchema,
  templateStyleSchema,
  useGenerateMultiplesampleAdministrativeDocumentPdf,
  useGetCertificateLogo,
  type ExportFormat,
} from "@/api/pdf";
import { useGetPermanentEnsembles } from "@/api/permanent-ensemble";
import * as AF from "@/components/form-items/AbstractForm";
import PDFViewer from "@/components/print/PDFViewer";
import SelectedRecord from "@/components/print/SelectedRecord";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CancelButton } from "@/components/ui/custom-buttons";
import { CustomDialogFooter } from "@/components/ui/custom-dialog";
import {
  CustomSheetBody,
  CustomSheetContent,
  CustomSheetHeader,
} from "@/components/ui/custom-sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  HeadlessFormItem,
} from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { PreviewDocumentUI } from "@/components/ui/preview-document-ui";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetClose,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabsContained";
import { useAuth } from "@/context/auth/AuthContext";
import i18n, { SupportedLocale } from "@/i18n";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import {
  getDefaultImage,
  MULTIPLESAMPLE_DOCUMENT_MAIN_BACK_POSITIONS,
  MULTIPLESAMPLE_DOCUMENT_PRIMARY_POSITION_EXCLUSIONS,
} from "@/lib/image";
import { cn, isEmptyRichText } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import {
  ChevronDown,
  FileDown,
  FileText,
  Loader2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const formSchema = z.object({
  documentType: administrativeDocumentTypeSchema,
  templateStyle: templateStyleSchema.default("standard"),
  showLogo: z.boolean().default(true),
  hasBeenExamined: z.boolean().default(false),
  signingInfo: z.string().min(1),
  documentNumber: z.string().optional(),
  additionalParagraph: z.string().optional(),
  showInscriptionsDos: z.boolean().default(false),
  showImageDos: z.boolean().default(false),
  hideDate: z.boolean().default(false),
  selectedAttachmentIds: z.array(z.string()).default([]),
});

type FormValues = z.infer<typeof formSchema>;

const STEPS = ["printOptions", "templateChoice", "preview"] as const;
type Step = (typeof STEPS)[number];

const TemplateCard = ({
  selected,
  onClick,
  label,
  variant,
}: {
  selected: boolean;
  onClick: () => void;
  label: string;
  variant: "standard" | "centered";
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "flex flex-col gap-3 items-center text-left cursor-pointer transition-all",
      "flex-1",
    )}
  >
    <span className="flex items-center gap-2 self-start">
      <span
        className={cn(
          "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border transition-colors",
          selected
            ? "border-primary bg-primary text-primary-foreground"
            : "border-slate-300 bg-white",
        )}
      >
        {selected && (
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            className="text-white"
          >
            <path
              d="M10 3L4.5 8.5L2 6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </span>
      <span className="text-sm font-medium">{label}</span>
    </span>
    <PreviewDocumentUI variant={variant} selected={selected} />
  </button>
);

/**
 * Certificat / document administratif d'un exemplaire d'estampe multiple.
 * Parcours aligné sur le certificat d'œuvre (options → modèle → aperçu).
 */
const ExemplaireCertificatDrawer = ({
  exemplaireId,
}: {
  exemplaireId: string;
}) => {
  const vt = useVisibleT();
  const { t } = useTranslation();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [activeStep, setActiveStep] = useState<Step>("printOptions");
  const [previewURL, setPreviewURL] = useState<string | null>(null);
  const createdFile = useRef<File>();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      documentType: "document1",
      templateStyle: "standard",
      showLogo: true,
      hasBeenExamined: false,
      signingInfo: "",
      documentNumber: "",
      additionalParagraph: "",
      showInscriptionsDos: false,
      showImageDos: false,
      hideDate: false,
      selectedAttachmentIds: [],
    },
  });

  const { mutateAsync: generatePdf, isPending: isGenerating } =
    useGenerateMultiplesampleAdministrativeDocumentPdf();
  const fileSaver = useUploadDocument();
  const { instanceData } = useAuth();
  const queryClient = useQueryClient();

  const { data: exemplaire } = useGetMultipleSampleById(exemplaireId);
  const { data: certificateLogoData } = useGetCertificateLogo();
  const hasLogo = Boolean(certificateLogoData?.logoUrl);

  const { data: permanentEnsembles } = useGetPermanentEnsembles();
  const selectedPermanentEnsemble = useMemo(
    () => permanentEnsembles?.at(0),
    [permanentEnsembles],
  );

  const { data: exemplaireAttachments = [] } = useGetAllAttachments({
    where: { docId: exemplaireId },
    order: { order: "asc" },
  });

  const image = useMemo(
    () => getDefaultImage(exemplaireAttachments, "multiplesample", "publicUrl"),
    [exemplaireAttachments],
  );

  const backImage = useMemo(
    () =>
      exemplaireAttachments.find((a) =>
        (
          MULTIPLESAMPLE_DOCUMENT_MAIN_BACK_POSITIONS as readonly string[]
        ).includes(a.position),
      ),
    [exemplaireAttachments],
  );

  const { data: secondaryAttachments = [] } = useGetAllAttachments({
    where: {
      docId: exemplaireId,
      position: {
        notIn: [...MULTIPLESAMPLE_DOCUMENT_PRIMARY_POSITION_EXCLUSIONS],
      },
    },
    order: { order: "asc" },
  });

  const watchShowImageDos = form.watch("showImageDos");
  const watchSelectedAttachmentIds = form.watch("selectedAttachmentIds");
  const isMaxSecondaryReached = watchSelectedAttachmentIds.length >= 2;

  useEffect(() => {
    if (!hasLogo) {
      form.setValue("showLogo", false);
    }
  }, [hasLogo, form]);

  useEffect(() => {
    if (!sheetOpen) {
      setActiveStep("printOptions");
      setPreviewURL(null);
      createdFile.current = undefined;
      form.reset();
    }
  }, [sheetOpen, form]);

  useEffect(() => {
    return () => {
      if (previewURL) URL.revokeObjectURL(previewURL);
    };
  }, [previewURL]);

  const fileBaseName = useMemo(() => {
    const num =
      exemplaire?.exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement ||
      exemplaire?.exemplaireDansEstampesMultiplesDescriptionNumero ||
      "exemplaire";
    return `${num}_Document Administratif`;
  }, [exemplaire]);

  const selectedRecordTitle = useMemo(() => {
    return [
      exemplaire?.exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement,
      exemplaire?.exemplaireDansEstampesMultiplesDescriptionNumero,
    ]
      .filter(Boolean)
      .join(" - ");
  }, [exemplaire]);

  const buildParams = useCallback(
    (values: FormValues) => ({
      documentType: values.documentType,
      templateStyle: values.templateStyle,
      oeuvreExaminee: values.hasBeenExamined,
      signloca: values.signingInfo,
      showLogo: values.showLogo && hasLogo,
      documentNumber: values.documentNumber ?? "",
      additionalParagraph: isEmptyRichText(values.additionalParagraph ?? "")
        ? ""
        : (values.additionalParagraph ?? ""),
      showInscriptionsDos: values.showInscriptionsDos,
      showImageDos: values.showImageDos,
      hideDate: values.hideDate,
      selectedAttachmentIds: values.selectedAttachmentIds,
    }),
    [hasLogo],
  );

  const runPreview = useCallback(async () => {
    const values = form.getValues();
    const file = await generatePdf({
      multiplesampleId: exemplaireId,
      lang: i18n.language as SupportedLocale,
      params: buildParams(values),
    });
    createdFile.current = file;
    if (previewURL) URL.revokeObjectURL(previewURL);
    setPreviewURL(URL.createObjectURL(file));
    setActiveStep("preview");
  }, [buildParams, exemplaireId, form, generatePdf, previewURL]);

  const handleNext = useCallback(async () => {
    if (activeStep === "printOptions") {
      const valid = await form.trigger("signingInfo");
      if (!valid) return;
      setActiveStep("templateChoice");
      return;
    }
    if (activeStep === "templateChoice") {
      await runPreview();
    }
  }, [activeStep, form, runPreview]);

  const handleSave = useCallback(
    async (format: ExportFormat) => {
      if (!instanceData) return;
      const values = form.getValues();
      const ext = format === "word" ? "docx" : "pdf";
      const mimetype =
        format === "word"
          ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          : "application/pdf";

      const saveAndDownload = (file: File | Blob) => {
        const asFile =
          file instanceof File
            ? file
            : new File([file], `${fileBaseName}.${ext}`, { type: mimetype });
        fileSaver.mutate(
          {
            file: asFile,
            data: {
              docId: exemplaireId,
              mimetype,
              sourceName: `${fileBaseName}.${ext}`,
              title: `${fileBaseName}.${ext}`,
              tag: "adminRecord",
              description: "",
            },
            s3bucket: instanceData.s3Bucket,
            instanceId: instanceData.id,
          },
          {
            onSuccess: () => {
              try {
                const url = URL.createObjectURL(asFile);
                const link = document.createElement("a");
                link.href = url;
                link.download = `${fileBaseName}.${ext}`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
              } catch (error) {
                console.error(error);
              }
              setSheetOpen(false);
              form.reset();
              queryClient.invalidateQueries({
                queryKey: [
                  "multiplesample",
                  exemplaireId,
                  "administrative-documents",
                ],
              });
            },
          },
        );
      };

      if (format === "pdf" && createdFile.current) {
        saveAndDownload(createdFile.current);
        return;
      }
      const file = await generatePdf({
        multiplesampleId: exemplaireId,
        lang: i18n.language as SupportedLocale,
        format,
        params: buildParams(values),
      });
      saveAndDownload(file);
    },
    [
      buildParams,
      exemplaireId,
      fileBaseName,
      fileSaver,
      form,
      generatePdf,
      instanceData,
      queryClient,
    ],
  );

  const activeStepIndex = STEPS.indexOf(activeStep);
  const isSaving = fileSaver.isPending || isGenerating;

  const getFooterButton = () => {
    switch (activeStep) {
      case "printOptions":
        return {
          label: t("next"),
          onClick: handleNext,
          disabled: false,
          loading: false,
        };
      case "templateChoice":
        return {
          label: t("administrativeDocumentsPrevisualiserBouton"),
          onClick: handleNext,
          disabled: isGenerating,
          loading: isGenerating,
        };
      case "preview":
        return {
          label: "",
          onClick: () => {},
          disabled: false,
          loading: false,
          isDropdown: true,
        };
    }
  };

  const footer = getFooterButton();

  return (
    <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
      <SheetTrigger asChild>
        <Button type="button" variant="outline" className="gap-2">
          <FileText className="h-4 w-4" aria-hidden />
          {vt("bo.print.mp.certificat", "Certificat")}
        </Button>
      </SheetTrigger>
      <CustomSheetContent className="w-[850px] sm:max-w-[850px]">
        <Form {...form}>
          <form
            onSubmit={(e) => e.preventDefault()}
            className="flex flex-col h-full"
          >
            <CustomSheetHeader>
              <SheetTitle>
                {vt(
                  "bo.print.mp.certificatTitle",
                  "Certificat de l'exemplaire",
                )}
              </SheetTitle>
              <p className="text-sm text-muted-foreground">
                {t("administrativeDocumentDescription")}
              </p>
            </CustomSheetHeader>

            <CustomSheetBody className="flex-1 overflow-y-auto">
              <div className="flex flex-col items-center gap-3 mb-6">
                <span className="font-semibold font-[Raleway] text-base">
                  {vt("bo.print.mp.exemplaireIncluded", "Exemplaire inclus")}
                </span>
                <SelectedRecord
                  image={image}
                  title={selectedRecordTitle || exemplaireId}
                />
              </div>

              <Separator className="mb-6" />

              <Tabs
                className="w-full flex flex-col items-center"
                value={activeStep}
                onValueChange={(v) => {
                  const step = v as Step;
                  const targetIndex = STEPS.indexOf(step);
                  if (targetIndex <= activeStepIndex) {
                    setActiveStep(step);
                  }
                }}
              >
                <TabsList className="h-9 mb-6">
                  {STEPS.map((step, index) => {
                    const isActive = activeStep === step;
                    const stepLabels: Record<Step, string> = {
                      printOptions: t("adminDocStepPrintOptions"),
                      templateChoice: t("adminDocStepTemplateChoice"),
                      preview: t("adminDocStepPreview"),
                    };
                    return (
                      <TabsTrigger
                        key={step}
                        value={step}
                        disabled={index > activeStepIndex}
                        className={cn(
                          "flex items-center gap-1.5 text-[11px] px-2.5 py-1.5",
                          !isActive && "bg-slate-100",
                        )}
                      >
                        <Badge
                          variant={isActive ? "outlinedActive" : "outlined"}
                          className="h-4 min-w-4 text-[10px] px-1 flex items-center justify-center leading-none"
                        >
                          {index + 1}
                        </Badge>
                        {stepLabels[step]}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>

                <TabsContent value="printOptions" className="w-full">
                  <AF.Col className="gap-4">
                    <Label className="text-lg">{t("printOptions")}</Label>

                    <AF.Col className="gap-2">
                      <FormLabel>{t("documentType")}</FormLabel>
                      <FormField
                        control={form.control}
                        name="documentType"
                        render={({ field }) => (
                          <HeadlessFormItem>
                            <FormControl>
                              <RadioGroup
                                onValueChange={field.onChange}
                                value={field.value}
                                className="flex flex-col"
                              >
                                <FormItem className="flex items-center space-x-3 space-y-0">
                                  <FormControl>
                                    <RadioGroupItem value="document1" />
                                  </FormControl>
                                  <FormLabel className="font-normal">
                                    {selectedPermanentEnsemble?.ensemblesTitreDocument1 ||
                                      vt("bo.print.mp.document1", "Document 1")}
                                  </FormLabel>
                                </FormItem>
                                <FormItem className="flex items-center space-x-3 space-y-0">
                                  <FormControl>
                                    <RadioGroupItem value="document2" />
                                  </FormControl>
                                  <FormLabel className="font-normal">
                                    {selectedPermanentEnsemble?.ensemblesTitreDocument2 ||
                                      vt("bo.print.mp.document2", "Document 2")}
                                  </FormLabel>
                                </FormItem>
                                {selectedPermanentEnsemble?.ensemblesTitreDocument3 && (
                                  <FormItem className="flex items-center space-x-3 space-y-0">
                                    <FormControl>
                                      <RadioGroupItem value="document3" />
                                    </FormControl>
                                    <FormLabel className="font-normal">
                                      {
                                        selectedPermanentEnsemble.ensemblesTitreDocument3
                                      }
                                    </FormLabel>
                                  </FormItem>
                                )}
                              </RadioGroup>
                            </FormControl>
                          </HeadlessFormItem>
                        )}
                      />
                      <AF.Checkbox
                        name="hasBeenExamined"
                        label={t("oeuvreExaminee")}
                      />
                    </AF.Col>

                    <div className="max-w-[400px]">
                      <AF.Text name="signingInfo" label={t("signloca")} />
                    </div>
                    <div className="max-w-[400px]">
                      <AF.Text
                        name="documentNumber"
                        label={t("documentNumber")}
                      />
                    </div>
                    <AF.RichText
                      name="additionalParagraph"
                      label={t("additionalParagraph")}
                    />

                    <Separator />

                    <FormField
                      control={form.control}
                      name="hideDate"
                      render={({ field }) => (
                        <FormItem className="flex items-center space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <FormLabel className="font-normal">
                            {t("hideDate")}
                          </FormLabel>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="showInscriptionsDos"
                      render={({ field }) => (
                        <FormItem className="flex items-center space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                          <FormLabel className="font-normal">
                            {t("showInscriptionsDos")}
                          </FormLabel>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="showImageDos"
                      render={({ field }) => (
                        <FormItem className="flex items-center space-x-3 space-y-0">
                          <FormControl>
                            <Checkbox
                              checked={field.value}
                              onCheckedChange={(checked) => {
                                field.onChange(checked);
                                if (checked) {
                                  form.setValue("selectedAttachmentIds", []);
                                }
                              }}
                              disabled={!backImage}
                            />
                          </FormControl>
                          <FormLabel
                            className={cn(
                              "font-normal",
                              !backImage && "text-muted-foreground",
                            )}
                          >
                            {t("showImageDos")}
                          </FormLabel>
                        </FormItem>
                      )}
                    />
                    {!backImage && (
                      <p className="text-sm text-muted-foreground ml-7">
                        {t("noBackImageAvailable")}
                      </p>
                    )}

                    {secondaryAttachments.length > 0 && (
                      <div
                        className={cn(
                          "flex flex-col gap-3",
                          watchShowImageDos &&
                            "opacity-50 pointer-events-none",
                        )}
                      >
                        <Label className="text-sm">
                          {t("selectSecondaryImages")} (
                          {watchSelectedAttachmentIds.length}/2)
                        </Label>
                        <div className="grid grid-cols-6 gap-3">
                          {secondaryAttachments.map((attachment) => {
                            const isSelected =
                              watchSelectedAttachmentIds.includes(
                                attachment.id,
                              );
                            const isDisabled =
                              !isSelected && isMaxSecondaryReached;

                            return (
                              <div
                                key={attachment.id}
                                className={cn(
                                  "relative aspect-square rounded-md overflow-hidden border-2 transition-all",
                                  isSelected
                                    ? "border-primary"
                                    : "border-slate-200",
                                  isDisabled
                                    ? "opacity-40 cursor-not-allowed"
                                    : "cursor-pointer hover:border-primary",
                                )}
                                onClick={() => {
                                  if (isDisabled) return;
                                  const current = form.getValues(
                                    "selectedAttachmentIds",
                                  );
                                  if (isSelected) {
                                    form.setValue(
                                      "selectedAttachmentIds",
                                      current.filter(
                                        (id) => id !== attachment.id,
                                      ),
                                    );
                                  } else {
                                    form.setValue("selectedAttachmentIds", [
                                      ...current,
                                      attachment.id,
                                    ]);
                                  }
                                }}
                              >
                                <img
                                  src={
                                    attachment.mediumPublicUrl ||
                                    attachment.smallPublicUrl ||
                                    ""
                                  }
                                  alt={
                                    attachment.textAlt?.[
                                      i18n.language as SupportedLocale
                                    ] || ""
                                  }
                                  className="w-full h-full object-cover"
                                />
                                <span className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[10px] text-center py-0.5 truncate px-1">
                                  {t(attachment.position)}
                                </span>
                                <div
                                  className="absolute top-2 right-2"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Checkbox
                                    checked={isSelected}
                                    disabled={isDisabled}
                                    onCheckedChange={(checked) => {
                                      const current = form.getValues(
                                        "selectedAttachmentIds",
                                      );
                                      const alreadySelected =
                                        current.includes(attachment.id);
                                      if (checked && !alreadySelected) {
                                        form.setValue(
                                          "selectedAttachmentIds",
                                          [...current, attachment.id],
                                        );
                                      } else if (
                                        checked === false &&
                                        alreadySelected
                                      ) {
                                        form.setValue(
                                          "selectedAttachmentIds",
                                          current.filter(
                                            (id) => id !== attachment.id,
                                          ),
                                        );
                                      }
                                    }}
                                    className="bg-white"
                                  />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </AF.Col>
                </TabsContent>

                <TabsContent value="templateChoice" className="w-full">
                  <AF.Col className="gap-6">
                    <FormField
                      control={form.control}
                      name="templateStyle"
                      render={({ field }) => (
                        <HeadlessFormItem>
                          <div className="flex gap-6">
                            <TemplateCard
                              variant="standard"
                              label={t("certificateTemplateStandard")}
                              selected={field.value === "standard"}
                              onClick={() => field.onChange("standard")}
                            />
                            <TemplateCard
                              variant="centered"
                              label={t("certificateTemplateCentered")}
                              selected={field.value === "centered"}
                              onClick={() => field.onChange("centered")}
                            />
                          </div>
                        </HeadlessFormItem>
                      )}
                    />

                    <Separator />

                    <AF.Col className="gap-2">
                      <FormField
                        control={form.control}
                        name="showLogo"
                        render={({ field }) => (
                          <FormItem className="flex items-center space-x-3 space-y-0">
                            <FormControl>
                              <Checkbox
                                checked={field.value}
                                onCheckedChange={field.onChange}
                                disabled={!hasLogo}
                              />
                            </FormControl>
                            <FormLabel
                              className={cn(
                                "font-normal",
                                !hasLogo && "text-muted-foreground",
                              )}
                            >
                              {t("certificateShowLogo")}
                            </FormLabel>
                          </FormItem>
                        )}
                      />
                      {!hasLogo && (
                        <p className="text-sm text-muted-foreground ml-7">
                          {t("certificateNoLogoInfo")}
                        </p>
                      )}
                    </AF.Col>
                  </AF.Col>
                </TabsContent>

                <TabsContent value="preview" className="w-full">
                  <div className="flex flex-col items-center gap-4">
                    {previewURL ? (
                      <div className="max-w-[650px] mx-auto max-h-[500px] overflow-y-auto border rounded-lg p-4 bg-slate-50">
                        <PDFViewer fileURL={previewURL} />
                      </div>
                    ) : (
                      <div className="flex items-center justify-center py-16">
                        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </CustomSheetBody>

            <CustomDialogFooter>
              <SheetClose asChild>
                <CancelButton className="flex-1" />
              </SheetClose>
              {activeStep === "preview" ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      className="flex-1"
                      type="button"
                      variant="default"
                      disabled={isSaving || !instanceData}
                    >
                      {isSaving && (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      )}
                      {t("saveDocument")}
                      <ChevronDown className="w-4 h-4 ml-2" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleSave("pdf")}>
                      <FileDown className="w-4 h-4 mr-2" />
                      {t("saveAsPdf")}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleSave("word")}>
                      <FileText className="w-4 h-4 mr-2" />
                      {t("saveAsWord")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button
                  className="flex-1"
                  type="button"
                  variant="default"
                  disabled={footer.disabled}
                  onClick={footer.onClick}
                >
                  {footer.loading && (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  )}
                  {footer.label}
                </Button>
              )}
            </CustomDialogFooter>
          </form>
        </Form>
      </CustomSheetContent>
    </Sheet>
  );
};

export default ExemplaireCertificatDrawer;
